import { OutgoingLink } from '@47ng/chakra-next'
import {
  Container,
  Heading,
  Icon,
  ListItem,
  Stack,
  Text,
  UnorderedList
} from '@chakra-ui/react'
import { ThirdPartyServicePrivacyCard } from 'client/components/legal/thirdPartyServicePrivacyCard'
import { PublicPage } from 'client/layouts/pages/publicPage'
import { execa } from 'execa'
import { dirs } from 'modules/std/fs/paths'
import { formatSVGDataURI } from 'modules/ui/format/media'
import { formatDate } from 'modules/ui/format/time'
import { DimmedText } from 'modules/ui/typography/dimmedText'
import { GetStaticProps } from 'next'
import React from 'react'
import { FiFileText, FiGitCommit } from 'react-icons/fi'

interface PrivacyPageProps {
  lastModifiedOn: string
}

export default function PrivacyPage({ lastModifiedOn }: PrivacyPageProps) {
  return (
    <>
      <PublicPage
        title="Privacy Policy"
        description="What data we collect, what we do with it, where and how we store it, and why."
        openGraph={
          {
            // todo: Add og:image
          }
        }
      >
        <Container as="main" maxW="3xl">
          <Heading as="h1" mt={12}>
            Privacy Policy
          </Heading>
          <DimmedText mt={2} mb={6}>
            <Icon as={FiFileText} mr={1} transform="translateY(2px)" />
            Last modification: {lastModifiedOn}
            <Icon
              as={FiGitCommit}
              transform="translateY(2px) rotateZ(90deg)"
              ml={4}
              mr={1}
            />
            <OutgoingLink href="#todo: Add links">
              Revision history
            </OutgoingLink>
          </DimmedText>
          <Text my={4}>
            Transparency in privacy is about explaining in simple terms what
            data is collected, when, for what purpose, and who/where it may be
            sent to, along with the choices you have over the data flow.
          </Text>
          <Heading as="h2" size="lg" mt={8} mb={4}>
            Actors
          </Heading>
          <Text my={4}>
            There are two kinds of people whose data interacts with the service:
          </Text>
          <UnorderedList my={4} spacing={2}>
            <ListItem>
              <b>Users</b> who have created an account on the service
            </ListItem>
            <ListItem>
              <b>Visitors</b> of the websites that use the service
            </ListItem>
          </UnorderedList>
          <Heading as="h2" size="lg" mt={8} mb={4}>
            Data Collection
          </Heading>
          <Heading as="h2" size="md" mt={8} mb={4}>
            On users
          </Heading>
          <Text my={4}>
            We ask of you the following information <b>on signup</b>:
          </Text>
          <UnorderedList my={4} spacing={2}>
            <ListItem>
              An <b>email address</b> to identify you and send you transactional
              emails about your account.
            </ListItem>
            <ListItem>
              An <em>optional</em> <b>display name</b> to personalise the UI,
              emails and humanise communication.{' '}
              <small>
                <em>Pseudonyms are welcome.</em>
              </small>
            </ListItem>
          </UnorderedList>
          <Text mb={4} mt={8}>
            Like any web-based service, we will also receive the following data
            automatically:
          </Text>
          <UnorderedList my={4} spacing={2}>
            <ListItem>
              Your <b>IP address</b>, which is anonymised and used for
              rate-limiting, abuse prevention and platform usage statistics.
            </ListItem>
            <ListItem>
              Your <b>browser type</b> and <b>operating system</b>, as part of
              the user-agent on every request to our API.
            </ListItem>
          </UnorderedList>
          <Heading as="h2" size="md" mt={8} mb={4}>
            On visitors
          </Heading>

          <Heading as="h2" size="lg" my={8}>
            Third Party Services
          </Heading>
          <Stack as="ul" spacing={8} mb={16}>
            <ThirdPartyServicePrivacyCard
              name="Clever Cloud"
              website="https://clever-cloud.com"
              image="https://www.clever-cloud.com/app/themes/cc-wp-theme/assets/img/brand-assets/square-svg.svg"
              what="All the data you provide when using the app"
              why="To provide the service"
              how="Data is stored end-to-end encrypted in our database"
              where="The service is hosted in Paris, France 🇫🇷"
              fundingModel="Subscriptions"
              privacyPolicyURL="https://www.clever-cloud.com/general-terms-and-conditions/"
            >
              <Text>
                The service is hosted on Clever Cloud. While they do not collect
                your data per se, they do provide the database which contains
                your data.
              </Text>
            </ThirdPartyServicePrivacyCard>
            <ThirdPartyServicePrivacyCard
              name="Stripe"
              website="https://stripe.com"
              image="https://images.ctfassets.net/fzn2n1nzq965/2EOOpI2mMZgHYBlbO44zWV/5a6c5d37402652c80567ec942c733a43/favicon.png?w=180&h=180"
              what="Your email address &amp; your name"
              when="When you checkout a paid plan"
              why="To send you payment receipts, invoices, and to reduce fraud"
              how="Data is sent using secure HTTPS connections to Stripe servers"
              where="Stripe has its servers in the USA 🇺🇸"
              fundingModel="VC, Subscriptions, FinTech"
              privacyPolicyURL="https://stripe.com/privacy"
            >
              <Text>
                We use Stripe as a payment processor to charge users for our
                paid plans.
              </Text>
              <DimmedText mt={2}>
                Your credit card information is handled by Stripe directly and
                never reaches our servers.
              </DimmedText>
            </ThirdPartyServicePrivacyCard>
            <ThirdPartyServicePrivacyCard
              name="Sentry"
              website="https://sentry.io"
              image={formatSVGDataURI(
                `<svg class="css-15xgryy e10nushx5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 66" width="400" height="367" style="background-color: rgb(255, 255, 255);"><path d="M29,2.26a4.67,4.67,0,0,0-8,0L14.42,13.53A32.21,32.21,0,0,1,32.17,40.19H27.55A27.68,27.68,0,0,0,12.09,17.47L6,28a15.92,15.92,0,0,1,9.23,12.17H4.62A.76.76,0,0,1,4,39.06l2.94-5a10.74,10.74,0,0,0-3.36-1.9l-2.91,5a4.54,4.54,0,0,0,1.69,6.24A4.66,4.66,0,0,0,4.62,44H19.15a19.4,19.4,0,0,0-8-17.31l2.31-4A23.87,23.87,0,0,1,23.76,44H36.07a35.88,35.88,0,0,0-16.41-31.8l4.67-8a.77.77,0,0,1,1.05-.27c.53.29,20.29,34.77,20.66,35.17a.76.76,0,0,1-.68,1.13H40.6q.09,1.91,0,3.81h4.78A4.59,4.59,0,0,0,50,39.43a4.49,4.49,0,0,0-.62-2.28Z" fill="#362d59" transform="translate(11, 11)"></path></svg>`
              )}
              what={
                <>
                  Your display name, browser &amp; system information
                  <DimmedText fontSize="xs">
                    Your email is never sent to Sentry.
                  </DimmedText>
                </>
              }
              when="When you encounter errors on our website"
              why="For us to reproduce the errors you encountered and follow-up"
              how="Data is sent using secure HTTPS connections to Sentry's servers"
              where="Sentry has its servers in the USA 🇺🇸"
              optOut={
                <>
                  You can request your display name not to be sent with error
                  reports{' '}
                  <DimmedText fontSize="xs">
                    <OutgoingLink textDecoration="underline">
                      Contact us
                    </OutgoingLink>
                  </DimmedText>
                </>
              }
              fundingModel="VC, Subscriptions"
              privacyPolicyURL="https://stripe.com/privacy"
            >
              <Text>
                We use Sentry to collect error reports from our servers and
                website.
              </Text>
            </ThirdPartyServicePrivacyCard>
          </Stack>
        </Container>
      </PublicPage>
    </>
  )
}

// --

export const getStaticProps: GetStaticProps<PrivacyPageProps> = async () => {
  const { stdout } = await execa(
    'git',
    [
      'log',
      '-1',
      '--format=%cd',
      '--date=short',
      import.meta.url.replace('file://', '')
    ],
    {
      cwd: dirs.repoRoot
    }
  )
  return {
    props: {
      lastModifiedOn: formatDate(stdout)
    }
  }
}
